import apiService from '@sonifi/wb-core/services/api.service';
import { ENDPOINTS } from '@sonifi/wb-core/services/endpointsConstants';
import {
  INCOMPLETE_STATUSES,
  isCanceledJob,
  isIncompleteJob,
  isSuccessfulJob,
  SUCCESS_STATUSES,
} from '@sonifi/wb-core/services/jobsHelper';
import { addTrailingSlash, parseQueryParams } from '@sonifi/wb-core/utils/parseHelper';
import { updateJobsStatus } from '@sonifi/wb-in-room-dining/store/menu/irdMenuSlice';

const pollingTime = 60 * 1000; // 1 minute;
const publishedStatusesFilter = Object.freeze({
  status__in: SUCCESS_STATUSES.concat(INCOMPLETE_STATUSES).join(','),
});

const getPublishJob = async (jobId) => {
  try {
    const url = `${ENDPOINTS.JOBS}/${jobId}/versions/1`;
    const res = await apiService.get(url);
    return res.data;
  } catch (error) {
    return {};
  }
};

const getLatestPublishJob = async ({ uri, overrides = null }) => {
  try {
    const params = {
      _order_by: '-created',
      rpp: 1,
      targets__contains: addTrailingSlash(uri),
      type: 'PUBLISH_IRD_MENU',
      ...overrides,
    };

    const url = `${ENDPOINTS.JOBS}?${parseQueryParams(params)}`;
    const res = await apiService.get(url);

    const latestPublishJob = res.data.objects.length
      ? res.data.objects[0]
      : null;
    return latestPublishJob;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const processLatestPublishJob =
  ({ job, irdMenuUri }) =>
  async (dispatch) => {
    if (!job) {
      return {
        lastPublishUser: null,
        lastPublishJob: null,
      };
    } else if (isIncompleteJob(job) || isCanceledJob(job)) {
      dispatch(setupPollingTimeout({ irdMenuUri }));
    }

    const lastPublishJob = {
      ...job,
      created: job.created,
    };

    const versionedJob = await getPublishJob(job.id);
    return {
      lastPublishUser: versionedJob._edited_by,
      lastPublishJob,
    };
  };

let pollingTimeout = null;

export const checkCleanPollingTimeout = () => {
  if (pollingTimeout) {
    clearTimeout(pollingTimeout);
  }
};

const setupPollingTimeout =
  ({ irdMenuUri }) =>
  async (dispatch) => {
    checkCleanPollingTimeout();
    pollingTimeout = setTimeout(async () => {
      const { latestPublishJob, lastPublished, lastPublishUser } =
        await dispatch(getJobsPublishStatus({ irdMenuUri }));
      dispatch(
        updateJobsStatus({ latestPublishJob, lastPublished, lastPublishUser }),
      );
    }, pollingTime);
  };

export const getJobsPublishStatus =
  ({ irdMenuUri }) =>
  async (dispatch) => {
    try {
      const response = {
        latestPublishJob: null,
        lastPublished: null,
        lastPublishUser: null,
      };
      const job = await getLatestPublishJob({ uri: irdMenuUri });
      const { lastPublishJob, lastPublishUser } = await dispatch(
        processLatestPublishJob({
          job,
          irdMenuUri,
        }),
      );

      response.lastPublishUser = lastPublishUser;
      response.latestPublishJob = lastPublishJob;

      if (isSuccessfulJob(lastPublishJob) || isIncompleteJob(lastPublishJob)) {
        response.latestPublishJob = null;
        response.lastPublished = lastPublishJob.created;
      } else {
        const latestPublishJob = await getLatestPublishJob({
          uri: irdMenuUri,
          overrides: publishedStatusesFilter,
        });
        response.latestPublishJob = latestPublishJob;
        response.lastPublished = latestPublishJob?.created || null;
      }

      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const saveCSVJob = async ({
  file,
  groupId,
  onCollide,
  revenueCenterId,
}) => {
  try {
    const body = {
      arguments: {
        on_collide: onCollide,
        group: `/groups/${groupId}`,
        revenue_center: `/revenue_centers/${revenueCenterId}`,
      },
      targets: [file.uri],
      type: 'IRD_BULK_IMPORT',
    };
    await apiService.post(ENDPOINTS.JOBS, body);
    return true;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};
